import type React from 'react';
import { useMemo } from 'react';

import {
  FeedbackStatusColumnHeader,
  SourceStatusColumnHeader,
} from '@/features/Feedback/FeedbackListView/FeedbackTableHeaders/headers';
import useGetDefaultFeedbackRowItemMap from '@/features/Feedback/hooks/useGetDefaultFeedbackRowItemMap';
import { useSentimentFilterMap } from '@/features/StructuredFilter/configs/useSentimentFilterMap';
import { useSourceFilterMap } from '@/features/StructuredFilter/configs/useSourceFilterMap';
import useStructuredFilterMap from '@/features/StructuredFilter/configs/useStructuredFilterMap';
import useUserRole from '@/hooks/domain/useUserRole';
import useFeedbackListStore from '@/stores/Feedback/FeedbackListStore';
import {
  ENGAGEMENT_COLUMNS,
  FEEDBACK_COLUMNS,
  SCORE_COLUMNS,
  SENTIMENT_COLUMNS,
  SOURCE_COLUMNS,
} from '@/types/filterKeyConst';
import { filterMap } from '@/utils/lazy';
import { getTimezoneDayjs } from '@/utils/time';

import useColumnConfigStore from '../stores/DataTable/ColumnConfigStore';

const useFeedbackListTableHeader = (): DataTableColumnT[] => {
  const { isEditor } = useUserRole();

  const { feedback_view } = useColumnConfigStore(
    (state) => state.columnConfigs,
  );

  const filterMap = useStructuredFilterMap();
  const sentimentFilterMap = useSentimentFilterMap();
  const sourceFilterMap = useSourceFilterMap();

  const defaultColumns: DataTableColumnT[] = useMemo(() => {
    let columnList: DataTableColumnT[] = [
      {
        columnKey: FEEDBACK_COLUMNS.TICKET,
        columnName: 'ID',
        width: 80,
        orderable: true,
        sortKey: FEEDBACK_COLUMNS.TICKET,
        visible: true,
        orderType: 'default',
        isDefaultColumn: true,
      },
      {
        columnKey: FEEDBACK_COLUMNS.COMPNAY,
        columnName: 'Account',
        width: 100,
        filterable: isEditor,
        searchable: true,
        visible: false,
      },
      {
        columnKey: FEEDBACK_COLUMNS.CUSTOMER,
        columnName: 'Contact',
        width: 100,
        filterable: isEditor,
        searchable: true,
        visible: false,
      },
      {
        columnKey: FEEDBACK_COLUMNS.AGENT,
        columnName: 'Agent',
        visible: false,
        filterable: isEditor,
        canNotBeShown: !isEditor,
        searchable: true,
        filterList: [],
      },
      {
        ...sourceFilterMap[SOURCE_COLUMNS.SOURCE_TYPE],
        visible: false,
        width: 200,
      },
      {
        columnKey: FEEDBACK_COLUMNS.TITLE,
        columnName: 'Title',
        width: 600,
        visible: false,
        responsive: true,
      },
      { ...filterMap[FEEDBACK_COLUMNS.CATEGORY] },
      {
        ...filterMap[FEEDBACK_COLUMNS.TAG],
        visible: false,
        width: 300,
      },
      {
        ...sentimentFilterMap[SENTIMENT_COLUMNS.KEY_SENTIMENT],
        visible: false,
      },
      {
        ...sentimentFilterMap[SENTIMENT_COLUMNS.ENDING_SENTIMENT],
        visible: false,
      },
      {
        ...filterMap[FEEDBACK_COLUMNS.PRIORITY],
        visible: false,
      },

      {
        columnKey: SOURCE_COLUMNS.SOURCE_TARGET,
        columnName: 'Source ID',
        visible: false,
      },
      {
        ...filterMap[FEEDBACK_COLUMNS.TAXONOMY_TOPIC],
        visible: false,
        canNotBeShown: true,
      },
      {
        columnKey: FEEDBACK_COLUMNS.DATE,
        columnName: 'Date',
        visible: false,
        orderable: true,
        sortKey: FEEDBACK_COLUMNS.DATE,
        orderType: 'date',
        sort: (a, b) => {
          if (!a || !b) return 0;
          if (a.rawData === '-' || b.rawData === '-') return 0;
          return (
            getTimezoneDayjs(a.rawData as string).unix() -
            getTimezoneDayjs(b.rawData as string).unix()
          );
        },
      },
      {
        ...sourceFilterMap[SOURCE_COLUMNS.DATA_ORIGIN],
        visible: false,
      },
      {
        ...filterMap[FEEDBACK_COLUMNS.REMARKS],
        visible: false,
      },
      {
        ...filterMap[SCORE_COLUMNS.RATING_SCORE],
        visible: false,
      },
      {
        ...filterMap[SCORE_COLUMNS.NPS_SCORE],
        visible: false,
      },
      {
        ...filterMap[SCORE_COLUMNS.CES_SCORE],
        visible: false,
      },
      {
        ...filterMap[SCORE_COLUMNS.CSAT_SCORE],
        visible: false,
      },
      {
        ...filterMap[FEEDBACK_COLUMNS.TICKET_STATUS],
        getHeaderNode: () => <SourceStatusColumnHeader />,
        width: 120,
        visible: false,
      },
      {
        ...filterMap[FEEDBACK_COLUMNS.STATUS],
        getHeaderNode: () => <FeedbackStatusColumnHeader />,
        width: 100,
        visible: false,
      },
      {
        ...filterMap[FEEDBACK_COLUMNS.IMPRESSION],
        width: 100,
        visible: false,
        orderable: true,
        sortKey: FEEDBACK_COLUMNS.IMPRESSION,
        orderType: 'number',
      },
      {
        ...filterMap[ENGAGEMENT_COLUMNS.ENGAGEMENT_RATE],
        width: 100,
        visible: false,
        orderable: true,
        sortKey: 'engagement.rate',
        orderType: 'number',
      },
      {
        ...filterMap[ENGAGEMENT_COLUMNS.TOTAL_ENGAGEMENT],
        width: 100,
        visible: false,
        orderable: true,
        sortKey: 'engagement.total',
        orderType: 'number',
      },
    ];

    if (!isEditor)
      columnList = columnList.filter(
        (col) => !['agent', 'customer', 'company'].includes(col.columnKey),
      );

    return columnList;
  }, [isEditor, sourceFilterMap, filterMap, sentimentFilterMap]);

  const columns = useMemo(() => {
    const fullColumns = [...defaultColumns];

    for (const config of feedback_view) {
      const targetIndex = fullColumns.findIndex(
        (col) => col.columnKey === config.name,
      );
      if (targetIndex === -1) continue;
      fullColumns[targetIndex] = {
        ...fullColumns[targetIndex],
        width: config.width,
        visible: config.visible,
        columnOrderIndex: config.order,
      };
    }

    return fullColumns;
  }, [defaultColumns, feedback_view]);

  return columns;
};

const useFeedbackTableData = (
  feedbackList: string[],
  options?: {
    onRowClick?: (alias: string | null, e?: React.MouseEvent) => void;
    onRowRightClick?: (alias: string, e: React.MouseEvent) => void;
  },
): DataTableRowT[] => {
  const feedbackMap = useFeedbackListStore((state) => state.feedbackMap);
  const getDefaultFeedbackRowItemMap = useGetDefaultFeedbackRowItemMap(
    (alias) => options?.onRowClick?.(alias),
  );

  const columns = useFeedbackListTableHeader();

  const data: DataTableRowT[] = useMemo(() => {
    return filterMap(feedbackList, (alias) => {
      const feedback = feedbackMap.get(alias);
      if (!feedback) {
        return null;
      }

      // Load a function which maps a feedback to row item
      const defaultRowItemMap = getDefaultFeedbackRowItemMap(feedback, alias);
      const rowItems: DataTableRowItemT[] = [];
      columns.map((column) => {
        const rowItem = defaultRowItemMap[column.columnKey];
        if (!rowItem) return;
        rowItems.push(rowItem);
      });

      return {
        rowKey: alias,
        onRowClick: options?.onRowClick,
        onRowRightClick: options?.onRowRightClick,
        rowData: rowItems,
      };
    });
  }, [
    columns,
    feedbackList,
    feedbackMap,
    getDefaultFeedbackRowItemMap,
    options?.onRowClick,
    options?.onRowRightClick,
  ]);

  return data;
};

export default useFeedbackListTableHeader;
export { useFeedbackTableData };
